import React, {useState} from 'react';
import classNames from 'classnames';

import {Button, Dropdown, Icon, Input, Label, MenuListItem, Text} from '@albert-io/atomic';
import {SALUTATIONS} from 'resources/augmented/User/user.extensions';

import OnboardingPageLayout from '../OnboardingPageLayout';
import type {PersonalInfoData} from '../onboarding.types';
import UsernameSuggestions from '../UsernameSuggestions';
import {useUsernameSuggestions} from '../useUsernameSuggestions';

interface PersonalInfoStepProps {
  onContinue: (data: PersonalInfoData) => void;
  /** Pre-fills the input — populated when SSO (Clever/Google) provides the name. */
  defaultFirstName?: string;
  /** Pre-fills the input — populated when SSO (Clever/Google) provides the name. */
  defaultLastName?: string;
}

export default function PersonalInfoStep({
  onContinue,
  defaultFirstName = '',
  defaultLastName = ''
}: PersonalInfoStepProps) {
  const [form, setForm] = useState<PersonalInfoData>({
    title: '',
    firstName: defaultFirstName,
    lastName: defaultLastName,
    username: ''
  });

  const {suggestions, isFormatValid, isUsernameAvailable, validationError} = useUsernameSuggestions(
    {
      firstName: form.firstName,
      lastName: form.lastName,
      username: form.username
    }
  );

  // Gate on a confirmed-available username (not merely "not known to be taken"), so an
  // unverified or taken username can't be submitted — the cause of the prod collisions.
  const isValid =
    form.title &&
    form.firstName.trim() &&
    form.lastName.trim() &&
    isFormatValid &&
    isUsernameAvailable;

  const handleChange =
    (field: keyof PersonalInfoData) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      const {value} = e.target;
      setForm((prev) => ({...prev, [field]: value}));
    };

  const setTitle = (value: string) => setForm((prev) => ({...prev, title: value}));
  const setUsername = (value: string) => setForm((prev) => ({...prev, username: value}));

  return (
    <OnboardingPageLayout>
      <Text className='text-xxl font-black'>Personal Information</Text>

      <div className='flex flex-col gap-x1-half'>
        <div className='flex flex-col'>
          <Label>Title</Label>
          <Dropdown
            fillWrapper
            wrapperClassName='mt-x1 w-full'
            trayClassName='w-full my-x0-half!'
            contentClassName='!max-w-none'
            trigger={
              <Button
                variant='outlined'
                className='flex w-full items-center! justify-between gap-x2 background-primary! px-x1-half! py-x1! aria-expanded:ring-2! aria-expanded:ring-border-selected! aria-expanded:ring-inset!'
                data-testid='title-dropdown'
              >
                <Text color={form.title ? 'primary' : 'tertiary'} className='font-normal!'>
                  {form.title || 'Select an Option'}
                </Text>
                <Icon icon='caret-down' color='secondary' />
              </Button>
            }
          >
            {({close}: {close: () => void}) => {
              const selectOption = (option: string) => {
                setTitle(option);
                close();
              };
              return (
                <div className='flex max-h-[320px] flex-col gap-y-x0-quarter overflow-y-auto p-x1'>
                  {SALUTATIONS.map((option: string) => {
                    const isSelected = form.title === option;
                    return (
                      <MenuListItem
                        key={option}
                        className={classNames(
                          'h-[37px] w-full shrink-0 items-center! justify-between! px-x1-half!',
                          {'background-info-subtle': isSelected}
                        )}
                        onClick={() => selectOption(option)}
                        data-testid={`title-option-${option}`}
                      >
                        <Text>{option}</Text>
                        {isSelected && <Icon icon='check' color='info' aria-hidden />}
                      </MenuListItem>
                    );
                  })}
                </div>
              );
            }}
          </Dropdown>
        </div>

        <div className='flex flex-col gap-x1-half m:flex-row m:gap-x2'>
          <div className='flex-1'>
            <Label>First Name</Label>
            <Input
              className='mt-x1'
              type='text'
              value={form.firstName}
              onChange={handleChange('firstName')}
              placeholder='e.g. Albert'
            />
          </div>
          <div className='flex-1'>
            <Label>Last Name (or School ID)</Label>
            <Input
              className='mt-x1'
              type='text'
              value={form.lastName}
              onChange={handleChange('lastName')}
              placeholder='e.g. Smith'
            />
          </div>
        </div>

        <div>
          <Label>Username</Label>
          <Input
            className='mt-x1'
            type='text'
            value={form.username}
            error={!!validationError}
            onChange={handleChange('username')}
            placeholder='e.g. albertsmith'
          />
          {validationError && (
            <Text size='s' color='negative' className='mt-x0-half'>
              {validationError}
            </Text>
          )}
          <UsernameSuggestions
            suggestions={suggestions}
            selectedUsername={form.username}
            onSelect={setUsername}
          />
        </div>
      </div>

      <Button
        className='w-full'
        color='primary'
        size='l'
        roundness='pill'
        disabled={!isValid}
        onClick={() => onContinue(form)}
      >
        <Text color='primary-inverse-static'>Continue</Text>
      </Button>
    </OnboardingPageLayout>
  );
}
